import { type ReactNode, type FC } from 'react';
import classNames from 'classnames';
import { CopyButton } from 'components/atoms/Copy/Copy.component';
import { CheckIconOutline, CopyOutlineIcon } from 'utils/icons';
import { useTranslation } from 'react-i18next';

interface PaymentInfoCardProps {
  /** Icon to display in the header */
  icon?: ReactNode;
  /** Title text for the header */
  title: string;
  /** Optional header class name */
  headerClassName?: string;
  /** Content to render inside the card */
  children: ReactNode;
  /** Optional wrapper class name */
  className?: string;
}

/**
 * Payment/banking information card component with header and bordered sections
 * Specifically designed for displaying payment details with copy functionality
 *
 * Used in:
 * - TopUpStep (activation flow) for payment details
 * - Similar banking/payment information displays
 *
 * Usage:
 * ```tsx
 * <PaymentInfoCard icon={<BankIcon />} title="Payment details">
 *   <PaymentInfoCard.Item title="IBAN" value="DE05..." />
 *   <PaymentInfoCard.Item title="Name" value="John Doe" />
 * </PaymentInfoCard>
 * ```
 */
export const PaymentInfoCard: FC<PaymentInfoCardProps> & {
  Item: typeof PaymentInfoCardItem;
} = ({ icon, title, headerClassName, children, className }) => {
  return (
    <div className={classNames('w-full', className)}>
      {/* Header */}
      <div
        className={classNames(
          '-mb-3 px-4 pt-3 pb-6 flex items-start font-semibold bg-primary-50 rounded-t-10',
          headerClassName,
        )}
      >
        {icon && <span className="w-5 h-5 mr-2 text-primary-400">{icon}</span>}
        <span>{title}</span>
      </div>

      {/* Content */}
      <div className="border shadow-checkbox border-primary-100 rounded-10 relative bg-white z-10">
        {children}
      </div>
    </div>
  );
};

interface PaymentInfoCardItemProps {
  /** Label/title for the info item */
  title: string;
  /** Value to display (must be string for copy functionality) */
  value: string | ReactNode;
  /** Whether to hide the copy button */
  hideCopyButton?: boolean;
  /** Custom class name for value container */
  valueClassName?: string;
  /** Whether this is the last item (removes bottom border) */
  isLast?: boolean;
}

/**
 * Individual row item for PaymentInfoCard
 * Matches the styling of tax component's InfoItem
 */
const PaymentInfoCardItem: FC<PaymentInfoCardItemProps> = ({
  title,
  value,
  hideCopyButton = false,
  valueClassName,
  isLast = false,
}) => {
  const { t } = useTranslation();
  const isStringValue = typeof value === 'string';

  return (
    <div
      className={classNames('flex-1 px-4 py-3', {
        'border-b border-b-primary-100': !isLast,
      })}
    >
      <div className="flex flex-col text-sm">
        <span className="text-light first-letter:capitalize">{title}</span>
        <span
          className={classNames('flex justify-between gap-2', valueClassName)}
        >
          <span className="break-words font-semibold whitespace-nowrap">
            {value}
          </span>
          {isStringValue && value && !hideCopyButton ? (
            <CopyButton
              textToCopy={value as string}
              renderTooltip={(copied) =>
                copied ? t('copied') : t('clipboard.copy_field', { title })
              }
              structure="text"
              color="main"
              render={(copied) =>
                copied ? (
                  <CheckIconOutline className="h-6 w-6 text-primary-400 flex-shrink-0" />
                ) : (
                  <CopyOutlineIcon className="h-6 w-6 text-primary-400 flex-shrink-0" />
                )
              }
            />
          ) : null}
        </span>
      </div>
    </div>
  );
};

PaymentInfoCard.Item = PaymentInfoCardItem;

export default PaymentInfoCard;
